import {
	AccountLevelEnum,
	AccountStatusEnum,
	AccountSubscriptionEnum,
	EnableDisableEnum,
	SnackBarVariants,
} from '@app/app.enum';
import withSnackBar from '@app/components/core/message.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import MultiSelectField, { IMultiSelectMenuItem } from '@app/components/shared/multi-select-field.component';
import Table from '@app/components/shared/table.component';
import * as models from '@app/models';
import { ISnackBarFeature } from '@app/models';
import injector from '@app/services';
import { AdminAccountService } from '@app/services/admin-account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Card, CardContent, Grid, Switch, TextField, Typography } from '@material-ui/core';
import Axios from 'axios';
import { debounce, sortBy } from 'lodash';
import * as queryString from 'querystring';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Column, UseTableCellProps } from 'react-table';

const intlService: IntlService = injector.get(IntlService);
const adminAccountService: AdminAccountService = injector.get(AdminAccountService);
const sharedService: SharedService = injector.get(SharedService);

const AccountLevelOverRides: React.FC<ISnackBarFeature> = ({ openSnackBar }) => {
	const cancelToken = Axios.CancelToken.source();
	const location = useLocation();
	const history = useHistory();
	const params = useParams<IAccountLevelOverridesParams>();
	const [isDataLoading, setLoading] = useState(false);
	const [customerSearchFieldValue, setCustomerSearchFieldValue] = useState('');
	const [subscriptionMenuItems, setSubscriptionMenuItems] = useState<IMultiSelectMenuItem[]>(Object.keys(AccountSubscriptionEnum).map((value: AccountSubscriptionEnum, index:number)=>{
		const menu : IMultiSelectMenuItem = {
			text : AccountSubscriptionEnum[value as keyof typeof AccountSubscriptionEnum],
			value,
			selected: true,
			isDefault: index === 0 ? true : false
		}
		return menu;
	}));

	const [accountStatusMenuItems, setAccountStatusMenuItems] = useState<IMultiSelectMenuItem[]>([
		{
			text: AccountStatusEnum.Active.toString(),
			value: AccountStatusEnum.Active.toString(),
			isDefault: true,
			selected: true,
		},
		{ text: AccountStatusEnum.TnCPending.toString(), value: AccountStatusEnum.TnCPending.toString(), selected: true },
	]);
	const [confirmationDialogState, setConfirmationDialogState] = useState(() => ({
		open: false,
		submitButton: {
			isSubmit: false,
			onSubmit: null,
			isDisabled: false,
			isLoading: false,
		},
		dialogId: '',
		dialogTitle: '',
		dialogDescription: '',
		dialogNoButton: intlService.GetFormattedMessage('app.common.dialog-cancel-button-text'),
		dialogYesButton: intlService.GetFormattedMessage('app.common.dialog-confirm-button-text'),
	}));
	const [selectedCheckBoxState, setSelectedCheckBoxState] = useState({
		accountId: '',
		state: EnableDisableEnum.Disabled,
	});
	const [accounts, setAccounts] = useState<models.IAccountServiceState[]>([]);

	useEffect(() => {
		const { serviceCode, carrierCode }: Partial<IAccountLevelOverRidesQueryParams> = queryString.parse(
			location.search.slice(1)
		);
		const validServiceTypes: TServiceType[] = ['service', 'specialService'];
		const isServiceTypeValid = !validServiceTypes.includes(params.serviceType);
		if (isServiceTypeValid || !serviceCode || !carrierCode) {
			history.goBack();
		}
	}, [params, location.search, history]);

	const fetchAccounts = useCallback(async () => {
		try {
			setLoading(true);
			const { serviceCode }: Partial<IAccountLevelOverRidesQueryParams> = queryString.parse(location.search.slice(1));
			const accountsResponse = await adminAccountService.getAllAccountForAdmin(
				SharedService.filterAndMap(
					accountStatusMenuItems,
					(x) => x.selected,
					(x) => x.value
				),
				SharedService.filterAndMap(
					subscriptionMenuItems,
					(x) => x.selected,
					(x) => x.value
				),
				customerSearchFieldValue,
				null,
				AccountLevelEnum.Customer,
				cancelToken.token
			);

			const disabledServiceNames: IDisableServiceName[] = await fetchDisabledServiceNames();

			const mappedAccounts = accountsResponse.map<models.IAccountServiceState>((x) => ({
				companyName: x.companyName,
				visibleAccountNumber: x.visibleAccountNumber,
				accountId: x.accountId,
				serviceState:
					disabledServiceNames.findIndex((y) => y.accountId === x.accountId && y.serviceCode === serviceCode) > -1,
			}));

			setAccounts(sortBy(mappedAccounts, (x) => x.serviceState));
		} catch (err) {
			if (Axios.isCancel(err)) {
				openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
			}
		} finally {
			setLoading(false);
		}
	}, [setLoading, location.search, accountStatusMenuItems, subscriptionMenuItems, customerSearchFieldValue]);

	const fetchDisabledServiceNames = useCallback(async () => {
		switch (params.serviceType) {
			case 'service':
				return fetchAccountCarrierServices();
			case 'specialService':
				return fetchAccountCarrierSpecialService();
			default:
				return [];
		}
	}, []);

	const fetchAccountCarrierServices = useCallback(async (): Promise<IDisableServiceName[]> => {
		const { carrierCode }: Partial<IAccountLevelOverRidesQueryParams> = queryString.parse(location.search.slice(1));
		const response: models.IAccountDisableService[] = await adminAccountService.getDisabledCarrierServices(
			'',
			cancelToken.token
		);
		return SharedService.filterAndMap(
			response,
			(x) => x.carrierCode === carrierCode,
			(x) => ({
				accountId: x.accountId,
				serviceCode: x.serviceID,
			})
		);
	}, [location.search]);

	const fetchAccountCarrierSpecialService = useCallback(async (): Promise<IDisableServiceName[]> => {
		const { carrierCode }: Partial<IAccountLevelOverRidesQueryParams> = queryString.parse(location.search.slice(1));
		const response: models.IAccountDisableSpecialService[] =
			await adminAccountService.getDisabledCarrierSpecialServices('', cancelToken.token);
		return SharedService.filterAndMap(
			response,
			(x) => x.carrierCode === carrierCode,
			(x) => ({
				accountId: x.accountId,
				serviceCode: x.specialServiceID,
			})
		);
	}, [location.search]);

	const updateServiceState = useCallback((accountId: string, status: EnableDisableEnum) => {
		switch (params.serviceType) {
			case 'service':
				return updateCarrierService(accountId, status);
			case 'specialService':
				return updateCarrierSpecialService(accountId, status);
		}
	}, []);

	const updateCarrierService = useCallback(async (accountId: string, status: EnableDisableEnum) => {
		const { serviceCode, carrierCode }: Partial<IAccountLevelOverRidesQueryParams> = queryString.parse(
			location.search.slice(1)
		);
		await adminAccountService.updateCarrierServiceState({
			accountId,
			details: [
				{
					serviceCode,
					message: intlService.GetFormattedMessage('admin.account-level-override.disable-message'),
					carrierCode,
					status,
				},
			],
		} as models.IAccountDisableEnableServiceRequest);
	}, []);

	const updateCarrierSpecialService = useCallback(async (accountId: string, status: EnableDisableEnum) => {
		try {
			const { serviceCode: specialServiceCode, carrierCode }: Partial<IAccountLevelOverRidesQueryParams> =
				queryString.parse(location.search.slice(1));
			await adminAccountService.updateCarrierSpecialServiceState({
				accountId,
				details: [
					{
						specialServiceCode,
						message: intlService.GetFormattedMessage('admin.account-level-override.disable-message'),
						carrierCode,
						status,
					},
				],
			});
		} catch (err) {
			if (Axios.isCancel(err)) {
				openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
			}
		}
	}, []);

	useEffect(() => {
		fetchAccounts();
	}, [subscriptionMenuItems, accountStatusMenuItems, customerSearchFieldValue]);

	const columns = useMemo(
		(): Array<Column<models.IAccountServiceState>> => [
			{
				accessor: 'companyName',
				Header: () => <></>,
				disableSortBy: true,
			},
			{
				accessor: 'visibleAccountNumber',
				Header: () => <></>,
				disableSortBy: true,
				width: 190,
			},
			{
				accessor: 'serviceState',
				Header: () => <></>,
				disableSortBy: true,
				width: 100,
				Cell: (tableData: UseTableCellProps<models.IAccountServiceState>) => (
					<Switch
						onChange={(e, checked) => handleOnSwitchChange(checked, tableData.row.original.accountId)}
						color='primary'
						checked={!tableData.value}
					/>
				),
			},
		],
		[]
	);

	const handleOnSwitchChange = (checked: boolean, accountId: string) => {
		const state = checked ? EnableDisableEnum.Enabled : EnableDisableEnum.Disabled;
		let dialogTitle = '';
		let dialogDescription = '';

		if (params.serviceType === 'service') {
			dialogTitle = intlService.GetFormattedMessage('app.service-label');
			dialogDescription = intlService.GetFormattedMessage(
				checked ? 'app.common.enable-confirm-dialog-text' : 'app.common.disable-confirm-dialog-text',
				{
					name: intlService.GetFormattedMessage('app.service-label'),
				}
			);
		} else {
			dialogTitle = intlService.GetFormattedMessage('app.special-service-label');
			dialogDescription = intlService.GetFormattedMessage(
				checked ? 'app.common.enable-confirm-dialog-text' : 'app.common.disable-confirm-dialog-text',
				{
					name: intlService.GetFormattedMessage('app.special-service-label'),
				}
			);
		}

		setSelectedCheckBoxState({ accountId, state });
		setConfirmationDialogState((prev) => ({
			...prev,
			open: true,
			dialogId: dialogTitle,
			dialogTitle,
			dialogDescription,
		}));
	};

	const handleOnSubscriptionMenuItemChange = (selectedValues: string[]) => {
		const updatedMenuItems = subscriptionMenuItems.slice();

		subscriptionMenuItems.forEach((item, i) => {
			updatedMenuItems[i].selected = selectedValues.includes(item.value);
		});

		setSubscriptionMenuItems(updatedMenuItems);
	};

	const handleOnAccountStatusMenuItemChange = (selectedValues: string[]) => {
		const updatedMenuItems = accountStatusMenuItems.slice();

		accountStatusMenuItems.forEach((item, i) => {
			updatedMenuItems[i].selected = selectedValues.includes(item.value);
		});

		setAccountStatusMenuItems(updatedMenuItems);
	};

	const handleCustomerNameChange = debounce((value: string) => {
		setCustomerSearchFieldValue(value);
	}, 800);

	const closeDialog = async (dialogPanel?: string, isCancel?: boolean) => {
		try {
			// If Confirm button of dialog is clicked then make an API call
			if (!isCancel) {
				setConfirmationDialogState((prev) => ({
					...prev,
					submitButton: {
						...prev.submitButton,
						isLoading: true,
						isDisabled: true,
					},
				}));

				await updateServiceState(selectedCheckBoxState.accountId, selectedCheckBoxState.state);
				const updatedAccounts = accounts.map((x) => {
					if (x.accountId === selectedCheckBoxState.accountId) {
						x.serviceState = !x.serviceState;
					}
					return x;
				});
				setAccounts(updatedAccounts);

				let messageId = '';

				if (params.serviceType === 'service') {
					messageId =
						selectedCheckBoxState.state === EnableDisableEnum.Enabled
							? 'app.snackbar.service-enabled-successfully'
							: 'app.snackbar.service-disabled-successfully';
				} else {
					messageId =
						selectedCheckBoxState.state === EnableDisableEnum.Enabled
							? 'app.snackbar.special-service-enabled-successfully'
							: 'app.snackbar.special-service-disabled-successfully';
				}

				openSnackBar(intlService.GetFormattedMessage(messageId), SnackBarVariants.Success);
				setSelectedCheckBoxState({ accountId: '', state: EnableDisableEnum.Disabled });
			}

			setConfirmationDialogState((prev) => ({ ...prev, open: false }));
		} catch (err) {
			if (!Axios.isCancel(err)) {
				openSnackBar(sharedService.parseError(err.data), SnackBarVariants.Error);
			}
		} finally {
			setConfirmationDialogState((prev) => ({
				...prev,
				submitButton: {
					...prev.submitButton,
					isLoading: false,
					isDisabled: false,
				},
			}));
		}
	};

	return (
		<Card>
			<CardContent>
				<CustomDialogBox closeCustomDialog={closeDialog} {...confirmationDialogState} />
				<Grid container={true} direction='column' spacing={4}>
					<Grid item={true}>
						<Typography variant='h6'>
							<FormattedMessage id='app.component.account-level-overrides' />
						</Typography>
					</Grid>
					<Grid item={true}>
						<Grid container={true} spacing={2}>
							<Grid item={true} xs={5}>
								<TextField
									fullWidth={true}
									label={intlService.GetFormattedMessage('admin.account-list-search-filter-label')}
									placeholder={intlService.GetFormattedMessage('admin.account-list-search-filter-placeholder-label')}
									variant='outlined'
									onChange={(e) => handleCustomerNameChange(e.target.value)}
								/>
							</Grid>
							<Grid item={true} xs={2}>
								<MultiSelectField
									id='subscriptionPlan'
									label={intlService.GetFormattedMessage('admin.customer-list.subscription-plan-label')}
									name='subscriptionPlan'
									saveButtonText={intlService.GetFormattedMessage('app.filter-done')}
									menuItems={subscriptionMenuItems}
									onSave={handleOnSubscriptionMenuItemChange}
								/>
							</Grid>
							<Grid item={true} xs={2}>
								<MultiSelectField
									id='accountStatus'
									label={intlService.GetFormattedMessage('app.customer-status')}
									name='accountStatus'
									saveButtonText={intlService.GetFormattedMessage('app.filter-done')}
									menuItems={accountStatusMenuItems}
									onSave={handleOnAccountStatusMenuItemChange}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true}>
						<Table<models.IAccountServiceState> columns={columns} data={accounts} dataLoading={isDataLoading} />
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};

interface IDisableServiceName {
	accountId: string;
	serviceCode: string;
}

interface IAccountLevelOverRidesQueryParams {
	carrierCode: string;
	serviceCode: string;
}

type TServiceType = 'specialService' | 'service';

interface IAccountLevelOverridesParams {
	serviceType: TServiceType;
}

export default withSnackBar(AccountLevelOverRides);



// WEBPACK FOOTER //
// ./src/components/admin/carriers/account-level-overrides.component.tsx